import { useEffect, useRef, useState } from 'preact/hooks';

interface SelectOption {
    value: string;
    label: string;
}

function CustomSelect({ componentName, value, options, onChange, className, ariaLabel, i18n }: {
    componentName: string;
    value: string;
    options: SelectOption[];
    onChange: (value: string) => void;
    className?: string;
    ariaLabel?: string;
    i18n: (key: string) => string;
}) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const selectedLabel = options.find((option) => option.value === value)?.label ?? '';

    useEffect(() => {
        if (!open) {
            return;
        }
        const handlePointerDown = (event: MouseEvent) => {
            if (!(event.target as HTMLElement).closest(`.${componentName}__select`)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (!open || !menuRef.current) {
            return;
        }
        const optionElements = menuRef.current.querySelectorAll<HTMLElement>(`.${componentName}__select-option`);
        const selectedIndex = options.findIndex((option) => option.value === value);
        optionElements[selectedIndex >= 0 ? selectedIndex : 0]?.focus();
    }, [open]);

    const focusOptionByOffset = (currentIndex: number, offset: number) => {
        if (!menuRef.current) {
            return;
        }
        const optionElements = menuRef.current.querySelectorAll<HTMLElement>(`.${componentName}__select-option`);
        if (optionElements.length === 0) {
            return;
        }
        const nextIndex = (currentIndex + offset + optionElements.length) % optionElements.length;
        optionElements[nextIndex]?.focus();
    };

    const selectOption = (optionValue: string) => {
        onChange(optionValue);
        setOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div className={`${componentName}__select ${open ? `${componentName}__select--open` : ''}${className ? ` ${className}` : ''}`}>
            <button
                ref={triggerRef}
                type="button"
                className={`${componentName}__select-trigger`}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => setOpen((previous) => !previous)}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                <span className={`${componentName}__select-value`}>{selectedLabel}</span>
            </button>
            {open && (
                <ul ref={menuRef} className={`${componentName}__select-menu`} role="listbox" aria-label={ariaLabel}>
                    {options.map((option, index) => (
                        <li
                            key={option.value}
                            role="option"
                            tabIndex={0}
                            aria-selected={option.value === value}
                            className={`${componentName}__select-option ${option.value === value ? 'active' : ''}`}
                            onClick={() => selectOption(option.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    selectOption(option.value);
                                } else if (event.key === 'ArrowDown') {
                                    event.preventDefault();
                                    focusOptionByOffset(index, 1);
                                } else if (event.key === 'ArrowUp') {
                                    event.preventDefault();
                                    focusOptionByOffset(index, -1);
                                }
                            }}
                        >
                            {i18n(option.label as string)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default CustomSelect;